import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  FormGroup,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FlexLayoutModule } from '@angular/flex-layout';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, finalize, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import {
  ComponentBase,
  SharedService,
  BusinessDetailsService,
  ErrorFormComponent,
  NumberOnlyDirective,
  SavePhasesDataType,
  POSITION,
  COUNTRY,
  UploadFileComponent,
} from 'src/app/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';

@Component({
  selector: 'app-business-details-step',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FlexLayoutModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatButtonModule,
    MatTooltipModule,
    NumberOnlyDirective,
    ErrorFormComponent,
    UploadFileComponent,
  ],
  templateUrl: './business-details-step.component.html',
  styleUrl: './business-details-step.component.scss',
})
export class BusinessDetailsStepComponent extends ComponentBase implements OnInit {
  /** When true, user came back from a later step (e.g. Working Hours); Skip is shown as disabled with a tooltip. */
  @Input() isFromPrevious = false;
  /** When true, the Skip button is shown in the header. */
  @Input() showSkip = true;
  /** Translation key for the step title. */
  @Input() titleKey = 'Complete_Your_Business_Profile';
  /** Optional translation key for the step description. */
  @Input() descriptionKey: string | undefined;
  /** Translation key for the submit button label. */
  @Input() submitLabelKey = 'Next';
  /** When false, hide internal action buttons (used when embedded in sidenav). */
  @Input() showActions = true;

  /** Emitted when user completes or skips this step (go to next). */
  @Output() next = new EventEmitter<any>();

  /** Reactive form for business details (role, phone, country, employees, branches, logo, tax). */
  form!: FormGroup;
  /** List of roles/positions for the PositionId dropdown. */
  positions: POSITION[] = [];
  /** List of countries for the CountryId dropdown. */
  countries: COUNTRY[] = [];
  /** Selected logo file when user uploads a new one. */
  businessLogoFile: File | null = null;
  /** URL or base64 of the current logo (from form or saved phase three data). */
  imgSrc = '';
  /** True when the existing logo is intentionally removed by user in edit mode. */
  logoRemoved = false;
  /** Calling code for the selected country (e.g. +966). */
  callingCode = '';
  /** Text direction: 'ltr' or 'rtl' based on current language. */
  dir = 'ltr';
  /** Loader flag for Skip request only (so Next isn't affected). */
  skipLoading = false;

  constructor(
    public readonly sharedService: SharedService,
    private readonly businessDetailsService: BusinessDetailsService,
    private readonly toastr: ToastrService,
    private readonly router: Router,
    readonly translateService: TranslateService
  ) {
    super();
    this.dir = this.sharedService.getLocalStorage('lang') === 'ar' ? 'rtl' : 'ltr';
    translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.dir = this.sharedService.getLocalStorage('lang') === 'ar' ? 'rtl' : 'ltr';
    });
  }

  ngOnInit(): void {
    this.initForm();
    this.getCountries();
    this.getPositions();
    this.loadPhaseThreeData();
    this.syncCountrySelectFromUserData();
    this.getCountryCallingCodeIfNeeded();
  }

  /** True when the phone country select is locked to phase-one `userData.CountryId` (user cannot change it). */
  get isPhoneCountrySelectLocked(): boolean {
    return this.sharedService.userData?.CountryId != null;
  }

  /** True when form is valid and not loading. */
  get canSubmit(): boolean {
    if (!this.form || this.loading) return false;
    return this.form.valid;
  }

  /** Builds the reactive form with all business-details controls and validators. */
  private initForm(): void {
    const regId = this.sharedService.registrationCycleLogId ?? null;
    this.form = new UntypedFormGroup({
      RegistrationCycleLogId: new UntypedFormControl(regId),
      PositionId: new UntypedFormControl(null, Validators.required),
      BusinessPhoneNumber: new UntypedFormControl(null, [
        Validators.required,
        Validators.minLength(4),
        Validators.maxLength(12),
      ]),
      NumberOfEmployees: new UntypedFormControl(1, [
        Validators.required,
        Validators.min(1),
      ]),
      NumberOfBranches: new UntypedFormControl(1, [
        Validators.required,
        Validators.min(1),
      ]),
      CountryId: new UntypedFormControl(null, Validators.required),
      SavePhasesDataType: new UntypedFormControl(SavePhasesDataType.Add),
      HaveTax: new UntypedFormControl(false),
      PriceIncludeTax: new UntypedFormControl(false),
      TaxRate: new UntypedFormControl(null),
    });
  }

  /** Loads countries list from shared service for the country selector. */
  private getCountries(): void {
    this.sharedService
      .getCountries()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.countries = res.Result ?? [];
      });
  }

  /**
   * When `userData.CountryId` exists, sets the phone country to that value and disables the control.
   * Otherwise leaves the control enabled so the user can pick a country.
   */
  private syncCountrySelectFromUserData(): void {
    const uid = this.sharedService.userData?.CountryId;
    if (uid != null) {
      this.form.patchValue({ CountryId: uid }, { emitEvent: false });
      this.form.get('CountryId')?.disable({ emitEvent: false });
    } else {
      this.form.get('CountryId')?.enable({ emitEvent: false });
    }
  }

  /** Updates form CountryId and fetches calling code when user selects a country. */
  onCountryChange(countryId: number): void {
    if (this.isPhoneCountrySelectLocked) return;
    this.form.patchValue({ CountryId: countryId });
    this.getCountryCallingCode(countryId);
  }

  /** Loads positions/roles list for the Role dropdown. */
  private getPositions(): void {
    this.businessDetailsService
      .getPositions()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.positions = res.Result ?? [];
      });
  }

  /** Prefill form from sharedService.businessDetails when available */
  private loadPhaseThreeData(): void {
    this.form.patchValue({ RegistrationCycleLogId: this.sharedService.registrationCycleLogId ?? null });
    const d = this.sharedService.businessDetails;
    if (!this.sharedService.businessDetails) return;

    if (this.showSkip) {
      // In case of modification: If the mobile number starts with zero, delete it and use the rest.
      const rawPhone = d.BusinessPhoneNumber != null ? String(d.BusinessPhoneNumber).trim() : '';
      const businessPhoneNumber = rawPhone.startsWith('0') ? rawPhone.slice(1) : rawPhone;

      this.form.patchValue({
        BusinessPhoneNumber: businessPhoneNumber,
        CountryId: d.CountryId,
        PositionId: d.PositionId,
        NumberOfEmployees: d.NumberOfEmployees || 1,
        NumberOfBranches: d.NumberOfBranches || 1,
      });
      this.imgSrc = d.BusinessLogoPath ?? '';
      if (d.CountryId) this.getCountryCallingCode(d.CountryId);

      this.form
        .get('SavePhasesDataType')
        ?.setValue(SavePhasesDataType.Edit);
    }
    if (this.sharedService.doneBusinessDetailsBySkip) {
      this.form
        .get('SavePhasesDataType')
        ?.setValue(SavePhasesDataType.Add);
    }
  }

  /** Sets calling code from current form CountryId if already selected (e.g. after loadPhaseThreeData). */
  private getCountryCallingCodeIfNeeded(): void {
    const cid = this.form?.get('CountryId')?.value;
    if (cid) {
      this.getCountryCallingCode(cid);
    }
  }

  /** Fetches and sets callingCode for the given country from countries list. */
  private getCountryCallingCode(countryId: number): void {
    this.sharedService
      .getCountries()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        const country = res.Result?.find((c) => c.Id === countryId);
        this.callingCode = country?.CallingCode ?? '';
      });
  }

  /** Returns the country object for the currently selected CountryId (for flag and calling code display). */
  getSelectedCountry(): { FlagPath?: string; CallingCode?: string } | undefined {
    const id = this.form?.get('CountryId')?.value;
    return this.countries?.find((c) => c.Id === id);
  }

  /** Stores the selected logo file from the upload component. */
  onGetFile(file: File | null): void {
    this.businessLogoFile = file;
    if (file) this.logoRemoved = false;
  }

  /** Clears current logo selection and marks saved logo for removal. */
  onClearLogo(): void {
    this.businessLogoFile = null;
    this.imgSrc = '';
    this.logoRemoved = true;
  }

  /** Shows a toast when the user selects a logo that exceeds the allowed size. */
  onFileSizeError(): void {
    this.toastr.warning(
      this.translateService.instant('Business_Logo_Size_Exceeded')
    );
  }

  /** Increases the given stepper field (NumberOfEmployees or NumberOfBranches) by one. */
  increment(field: 'NumberOfEmployees' | 'NumberOfBranches'): void {
    const ctrl = this.form.get(field);
    if (ctrl) {
      const v = (ctrl.value ?? 0) + 1;
      ctrl.setValue(v);
    }
  }

  /** Decreases the given stepper field by one, with a minimum of 1. */
  decrement(field: 'NumberOfEmployees' | 'NumberOfBranches'): void {
    const ctrl = this.form.get(field);
    if (ctrl) {
      const v = Math.max(1, (ctrl.value ?? 0) - 1);
      ctrl.setValue(v);
    }
  }

  /** Calls skip API for phase 3 and emits next on success; handles expired session. */
  onSkip(): void {
    const regId = this.sharedService.getCookie('RegistrationCycleLogId');
    if (!regId) {
      this.toastr.warning(this.translateService.instant('Registration_Session_Expired'));
      this.router.navigate(['registration-cycle']);
      return;
    }

    if (
      this.sharedService.doneBusinessDetailsBySkip &&
      this.isFromPrevious
    ) {
      this.toastr.warning(this.translateService.instant('Business_Details_Step_Skipped_Before'));
      this.next.emit();
      return;
    }

    if (
      !this.sharedService.doneBusinessDetailsBySkip &&
      this.sharedService.businessDetails &&
      this.isFromPrevious
    ) return;

    this.skipLoading = true;
    this.businessDetailsService
      .skip({
        RegistrationCycleLogId: +regId,
        PhaseNumber: 3,
      } as { RegistrationCycleLogId: number; PhaseNumber: number })
      .pipe(
        take(1),
        tap((res) => {
          if (this.sharedService.checkResponseStatus(res.Message, res.State)) {
            this.sharedService.playSound('assets/sounds/done.mp3');
            this.next.emit({ doneBusinessDetailsBySkip: true });
          }
        }),
        finalize(() => (this.skipLoading = false))
      )
      .subscribe();
  }

  /** Entry point for form submit; delegates to onSubmit. */
  submit(): void {
    this.onSubmit();
  }

  /** Returns true when in edit mode and form values match saved business details (no changes). Shows "No data has been changed" toast and allows caller to skip API submit. */
  get isBusinessDetailsUnchanged(): boolean {
    if (!this.sharedService.businessDetails || !this.showSkip) return false;
    const d = this.sharedService.businessDetails;
    const rawFormPhone = this.form.get('BusinessPhoneNumber')?.value != null ? String(this.form.get('BusinessPhoneNumber')?.value).trim() : '';
    const formPhone = rawFormPhone.startsWith('0') ? rawFormPhone.slice(1) : rawFormPhone;
    const rawSavedPhone = d.BusinessPhoneNumber != null ? String(d.BusinessPhoneNumber).trim() : '';
    const savedPhone = rawSavedPhone.startsWith('0') ? rawSavedPhone.slice(1) : rawSavedPhone;

    const formCountryId = this.form.get('CountryId')?.value ?? null;
    const formPositionId = this.form.get('PositionId')?.value ?? null;
    const formEmployees = this.form.get('NumberOfEmployees')?.value ?? null;
    const formBranches = this.form.get('NumberOfBranches')?.value ?? null;

    const samePhone = formPhone === savedPhone;
    const sameCountry = (formCountryId != null && d.CountryId != null && +formCountryId === +d.CountryId) || (formCountryId == null && d.CountryId == null);
    const samePosition = (formPositionId != null && d.PositionId != null && +formPositionId === +d.PositionId) || (formPositionId == null && d.PositionId == null);
    const sameEmployees = (formEmployees != null && d.NumberOfEmployees != null && +formEmployees === +d.NumberOfEmployees) || (formEmployees == null && d.NumberOfEmployees == null);
    const sameBranches = (formBranches != null && d.NumberOfBranches != null && +formBranches === +d.NumberOfBranches) || (formBranches == null && d.NumberOfBranches == null);

    const savedLogoPath = (d.BusinessLogoPath ?? '').trim();
    const currentImgSrc = (this.imgSrc ?? '').trim();
    const sameLogo = this.businessLogoFile === null && currentImgSrc === savedLogoPath;

    if (samePhone && sameCountry && samePosition && sameEmployees && sameBranches && sameLogo) {
      this.toastr.warning(this.translateService.instant('No_Data_Has_Been_Changed'));
      return true;
    }
    return false;
  }

  /** Validates form, builds FormData with logo and payload, calls savePhaseTwo then getRegisteredPhases and emits next on success. Handles unchanged data and expired session. */
  private onSubmit(): void {
    this.form.markAllAsTouched();

    if (!this.form || this.form.invalid) return;

    if (this.isBusinessDetailsUnchanged) {
      this.next.emit();
      return;
    }

    this.loading = true;
    const formData = new FormData();
    if (this.businessLogoFile) formData.append('BusinessLogoFile', this.businessLogoFile);
    const regId = this.sharedService.getCookie('RegistrationCycleLogId');
    if (!regId) {
      this.loading = false;
      this.toastr.warning(this.translateService.instant('Registration_Session_Expired'));
      this.router.navigate(['registration-cycle']);
      return;
    }
    const payload: any = {
      ...this.form.getRawValue(),
      RegistrationCycleLogId: String(regId),
    };

    // Normalize BusinessPhoneNumber (remove leading zero if exists)
    const rawBusinessPhone = this.form.get('BusinessPhoneNumber')?.value?.toString() ?? '';
    payload.BusinessPhoneNumber =
      rawBusinessPhone.startsWith('0') && rawBusinessPhone.length > 1
        ? rawBusinessPhone.substring(1)
        : rawBusinessPhone;

    if (this.sharedService.businessDetails && this.showSkip) {
      payload.BusinessLogoName = this.sharedService.businessDetails.BusinessLogoName;
      payload.BusinessLogoFolderName = this.sharedService.businessDetails.BusinessLogoFolderName;
      if (this.logoRemoved) {
        payload.BusinessLogoName = '';
        payload.BusinessLogoFolderName = '';
      }
    }

    formData.append('RegistrationCyclePhaseThreeModelStirng', JSON.stringify(payload));

    this.businessDetailsService
      .savePhaseTwo(formData)
      .pipe(
        take(1),
        filter((res) => this.sharedService.checkResponseStatus(res.Message, res.State)),
        switchMap(() =>
          this.businessDetailsService.getRegisteredPhases({ RegistrationCycleLogId: +regId }).pipe(
            tap((res) => {
              this.sharedService.playSound('assets/sounds/done.mp3');
              this.next.emit();
              if (res.State != 100) this.toastr.error(res.Message);
            })
          )
        ),
        finalize(() => (this.loading = false))
      )
      .subscribe();
  }
}

